import { UserKanbanBoardRole } from "@/generated/graphql";

/**
 * The board roles a client may actually ASSIGN.
 *
 * SE-8296. OWNER is absent on purpose, and the absence is a server fact rather
 * than a product preference:
 *
 * - `addUserToKanbanBoard` rejects `role: OWNER` with BadRequestException("There
 *   can only be one owner per board").
 * - `updateUserKanbanBoardRole` rejects the same promotion with that message,
 *   and rejects ANY edit to the incumbent's row with BadRequestException("Cannot
 *   change the owner's role").
 *
 * A partial unique index on the junction table enforces at most one OWNER, so
 * there is no request shape in which a second one could be written. Offering
 * Owner in a dropdown therefore produced a control whose only possible outcome
 * was a red toast. Ownership moves through `transferKanbanBoardOwnership`,
 * which demotes the incumbent to EDITOR and promotes the target inside one
 * locked transaction — see `useBoardOwnershipTransfer`.
 */
export const ASSIGNABLE_BOARD_ROLES: readonly UserKanbanBoardRole[] = [
  UserKanbanBoardRole.Editor,
  UserKanbanBoardRole.Viewer,
];

/**
 * Human labels for the roles a board row can display, including OWNER.
 *
 * OWNER is displayable even though it is not assignable — the owner's row still
 * has to say "Owner".
 */
export const BOARD_ROLE_LABELS: Record<UserKanbanBoardRole, string> = {
  [UserKanbanBoardRole.Owner]: "Owner",
  [UserKanbanBoardRole.Editor]: "Editor",
  [UserKanbanBoardRole.Viewer]: "Viewer",
};
